import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Button, Upload, message, Spin, List, Divider, Image, Card, Collapse,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { TbReportSearch } from 'react-icons/tb';
import { FaXmark, FaLock, FaUnlock } from 'react-icons/fa6';

import drEasyApi from '../../../Api/Customapi';
import getErrorMessage from '../../../hooks/getErrorMssg';
import { getImg } from '../../../Api/getImg';
import ReportNoteTabs from './ReportNoteTabs';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { CiUnlock } from 'react-icons/ci';
import { useQueryClient } from '@tanstack/react-query';

const ReportModal = ({
  visible,
  onCancel,
  bookingId,
  reportId: initialReportId,
  onSuccess,
  isPrivateReport,
  setCurrentReportBooking,
  setCurrentReportId         
}) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reportId, setReportId] = useState(initialReportId);
  const [photoIdPreview, setPhotoIdPreview] = useState(null);
  const [isNewReport, setIsNewReport] = useState(false);
  const [notes, setNotes] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingFields, setEditingFields] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);
const newReportIdRef = useRef(null); 
const queryClient = useQueryClient(); 
console.log(reportId);

  useEffect(() => {
    setReportId(initialReportId);
    setEditingNoteId(null);
    setEditingFields(null);
  }, [initialReportId]);

  useEffect(() => {
    if (visible) {
      if (reportId) {
        setIsNewReport(false);
        fetchNotes(reportId);
      } else {
        setIsNewReport(true);
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [visible, reportId]);

  const resetForm = () => {
    setNotes([]);
    setPhotoIdPreview(null);
    setLoading(false);
    setUploading(false);
    setEditingNoteId(null);
    setEditingFields(null);
  };

  const fetchNotes = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await drEasyApi.get(`/api/Reports/NotesByReportId?ReportId=${id}`);
      setNotes(res.data || []);
    } catch (err) {
      console.error('Failed to fetch notes', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await drEasyApi.post('/api/Storage/UploadImage', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoIdPreview(res.data.id);
      onSuccess();
      message.success('Image uploaded');
    } catch (err) {
      onError(err);
      message.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleNoteSubmit = async (fields) => {
    try {
      setLoading(true);
      const notePayload = {
        diagnosis: isPrivateReport ? "" : fields.diagnosis,
        meds: isPrivateReport ? "" : fields.medications,
        followUp: fields.followUp,
        sessions: isPrivateReport ? "" : fields.sessions,
        notes: isPrivateReport ? "" : fields.notes,
        photoId: photoRemoved ? "" : (photoIdPreview || ""),
        isPrivate: isPrivateReport ? true : false
      };


      if (editingNoteId) {
        await drEasyApi.put(`/api/Reports/EditNote`, {
          noteId: editingNoteId,
          ...notePayload,
        });
        message.success('Note updated');
        await fetchNotes(reportId);
      } else if (isNewReport) {
        const res = await drEasyApi.post('/api/Reports/AddReport', {
          bookingId,
          notes: [notePayload],
        });
        const newReportId = res.data.id;
        const newNoteId = res.data.noteId || crypto.randomUUID();
        setReportId(newReportId);
        setIsNewReport(false);
        const newNote = {
          id: newNoteId,
          ...notePayload,
          createdAt: new Date().toISOString(),
        };
        setNotes([newNote]);
        message.success('Report created');
      } else {
        const res = await drEasyApi.post('/api/Reports/AddNotesToReport', {
          reportId,
          ...notePayload,
        });
        const newNote = {
          id: res.data?.noteId || crypto.randomUUID(),
          ...notePayload,
          createdAt: new Date().toISOString(),
        };
        setNotes((prev) => [...prev, newNote]);
        message.success('Note added');
        await fetchNotes(reportId);
      }

      setPhotoIdPreview(null);
      setPhotoRemoved(false);
      setEditingNoteId(null);
      setEditingFields(null);
      onSuccess?.();
    } catch (err) {
      message.error('Failed to save: ' + getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (note) => {
    setEditingNoteId(note.id);
    setEditingFields({
      diagnosis: note.diagnosis || '',
      medications: note.meds || '',
      followUp: note.followUp || '',
      sessions: note.sessions || '',
      notes: note.notes || '',
    });
    setPhotoIdPreview(note.photoId || null);
    setPhotoRemoved(false);
  };

const handleDelete = async (noteId) => {
  Modal.confirm({
    title: 'Delete Note',
    content: 'Are you sure you want to delete this note?',
    okText: 'Yes',
    cancelText: 'No',
    okType: 'danger',
    onOk: async () => {
      try {
        await drEasyApi.delete(`/api/Reports/DeleteNote?noteId=${noteId}`);
        const remaining = notes.filter((n) => n.id !== noteId);
        setNotes(remaining);

        // ✅ If it’s the last note and it's a newly created report
        if (remaining.length === 0 && reportId === newReportIdRef.current) {
          await drEasyApi.delete(`/api/Reports/DeleteReport?id=${reportId}`);
          message.success('Report deleted');

          setReportId(null);
          setIsNewReport(true);
          newReportIdRef.current = null;

          setCurrentReportId?.('');
          setCurrentReportBooking?.((prev) => ({
            ...prev,
            reportId: null,
          }));

          await queryClient.invalidateQueries({
            queryKey: ['bookings'],
            refetchType: 'active',
          });

          onCancel();
        }
      } catch (err) {
        message.error('Failed to delete note');
      }
    },
  });
};



  const togglePrivacy = async (noteId, currentStatus) => {
    try {
      setLoading(true);
      const note = notes.find(n => n.id === noteId);
      if (!note) return;

      const updatedNote = {
        noteId: note.id,
        diagnosis: note.diagnosis || '',
        meds: note.meds || '',
        followUp: note.followUp || '',
        sessions: note.sessions || '',
        notes: note.notes || '',
        photoId: note.photoId || '',
        isPrivate: !currentStatus,
      };

      await drEasyApi.put(`/api/Reports/EditNote`, updatedNote);
      setNotes((prev) =>
        prev.map((n) =>
          n.id === noteId ? { ...n, isPrivate: !currentStatus } : n
        )
      );
      message.success(`Note is now ${!currentStatus ? 'Private 🔒' : 'Public 🔓'}`);
    } catch (err) {
      message.error('Failed to toggle privacy');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-1">
          <TbReportSearch className="text-2xl" />
          <span>Medical Report</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      centered
      destroyOnClose
    >
      <Spin spinning={loading}>
        <ReportNoteTabs
          isPrivate={isPrivateReport}
          onSubmit={handleNoteSubmit}
          editingFields={editingFields}
          onClearEdit={resetForm}
          photoIdPreview={photoIdPreview}
          setPhotoIdPreview={(id) => {
            setPhotoIdPreview(id);
            if (!id) setPhotoRemoved(true); // Mark as removed
          }}
          setPhotoRemoved={setPhotoRemoved}
          uploading={uploading}
          handleUpload={handleUpload}
        />




        {!isNewReport && notes.length > 0 && (
          <>
            <Divider orientation="center">Report Notes</Divider>
            <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 8 }}>
              {notes.filter((n) => n.isPrivate === isPrivateReport).length === 0 ? (
                <p style={{ textAlign: 'center', color: '#999' }}>
                  {isPrivateReport ? 'No private notes available.' : 'No public notes available.'}
                </p>
              ) : (
                <List
                  itemLayout="vertical"
                  dataSource={notes.filter((note) => note.isPrivate === isPrivateReport)}
                  renderItem={(note) => (
                    <List.Item
                      key={note.id}
                      style={{
                        marginBottom: 16,
                        borderRadius: 8,
                        border: "none",
                        padding: 16,
                      }}
                    >
                      <Card
                        style={{
                          padding: "3px",
                          backgroundColor: '#fdfdfd',
                          boxShadow: '0 0 10px rgba(0, 123, 255, 0.2)', // light shadow with primary tone
                          borderRadius: 8,
                        }}
                        size="small"
                        bordered={false}

                        title={
                          <div className="flex justify-between items-center">
                            <span className="font-semibold text-lg text-primary">
                             {isPrivateReport?"Private note":`Diagnosis: ${note.diagnosis || '—'}`} 
                            </span>
                            <div className="flex items-center gap-2">
                              <Button
                                type="text"
                                icon={
                                  note.isPrivate ? (
                                    <FaLock className="text-red-500" />
                                  ) : (
                                    <CiUnlock className="text-green-600 text-xl" />
                                  )
                                }
                                onClick={() => togglePrivacy(note.id, note.isPrivate)}
                                title={note.isPrivate ? 'Private' : 'Public'}
                              />
                              <Button type="link" onClick={() => handleEdit(note)} size="small">
                                <FaEdit />

                              </Button>
                              <Button danger type="link" size="small" onClick={() => handleDelete(note.id)}>
                                <FaTrash />

                              </Button>
                            </div>
                          </div>
                        }
                      >
                        <div style={{ display: 'flex', gap: 16 }}>
                          <div style={{ flex: 1 }}>
                            {isPrivateReport ? (<Collapse ghost>


                              <Collapse.Panel key="3" header="Follow-Up">
                                {note.followUp || '—'}
                              </Collapse.Panel>


                            </Collapse>) : (<Collapse ghost>

                              <Collapse.Panel key="2" header="Medications">
                                {note.meds || '—'}
                              </Collapse.Panel>
                              <Collapse.Panel key="3" header="Follow-Up">
                                {note.followUp || '—'}
                              </Collapse.Panel>
                              <Collapse.Panel key="4" header="Sessions">
                                {note.sessions || '—'}
                              </Collapse.Panel>
                              <Collapse.Panel key="5" header="Additional Notes">
                                {note.notes || '—'}
                              </Collapse.Panel>
                            </Collapse>)}

                          </div>

                          {note.photoId && (
                            <div style={{ minWidth: 200 }}>
                              <Image
                                src={getImg(note.photoId)}
                                alt="Attached"
                                style={{
                                  width: 200,
                                  height: 'auto',
                                  borderRadius: 6,
                                  border: '1px solid #eee',
                                }}
                              />
                            </div>
                          )}
                        </div>

                      </Card>
                    </List.Item>
                  )}
                />
              )}
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default ReportModal;
